import { ref, watch, computed, reactive, getCurrentInstance, nextTick } from 'vue';
import { getRouteQueryParam } from '@/helpers/routeQuery';

export interface QueryFilterOptions {
  /** Convert an internal committed value to a URL query string value. */
  toQuery: (internalValue: string) => string;
  /** Parse a URL query string value to an internal committed value. Return null if invalid/unrecognized. */
  fromQuery: (queryValue: string) => string | null;
}

export interface QueryFilter {
  /** Current committed internal value. `null` means "no filter" / default. */
  readonly value: string | null;
  /** Whether a non-default filter is active. */
  readonly isApplied: boolean;
  /** Set a new internal value and sync the URL. Returns `true` if the value changed. */
  apply(newInternalValue: string | null): boolean;
}

/**
 * Bidirectional sync between a single URL query parameter and reactive component state.
 *
 * Must be called inside `setup()`. Accesses `$route` / `$router` from the component instance
 * so it works with both a real Vue Router install and test-utils `mocks`.
 */
function getRouterContext() {
  const instance = getCurrentInstance();
  const vm = instance?.proxy as any;
  const globals = instance?.appContext.config.globalProperties as any;
  const route = vm?.$route ?? globals?.$route;
  const router = vm?.$router ?? globals?.$router;

  if (!route) {
    throw new Error('useUrlQueryParamFilter requires $route on the component instance or globalProperties');
  }

  return {
    route,
    router: router ?? { replace: () => Promise.resolve().catch(() => {}) },
  };
}

export function useUrlQueryParamFilter(paramName: string, options: QueryFilterOptions): QueryFilter {
  const { route, router } = getRouterContext();

  const initialQueryStr = getRouteQueryParam(route.query, paramName);
  const initialValue = initialQueryStr !== null ? options.fromQuery(initialQueryStr) : null;

  const _value = ref<string | null>(initialValue);

  function syncToUrl(): void {
    const desired = _value.value !== null ? options.toQuery(_value.value) : null;
    const current = getRouteQueryParam(route.query, paramName);

    if (desired === current) return;

    const query = { ...route.query };
    if (desired === null) {
      delete query[paramName];
    } else {
      query[paramName] = desired;
    }
    router.replace({ path: route.path, query }).catch(() => {});
  }

  // After parsing the initial query into `_value`, the URL may still need adjustment (invalid param to remove,
  // or valid but non-canonical casing). syncToUrl adjusts that. nextTick defers replace until after setup
  // so we don't navigate in the middle of component initialization.
  nextTick(syncToUrl);

  // When this param changes on the route (browser back/forward, programmatic `router.replace`, etc.),
  // re-parse into `_value` so list queries and UI stay aligned. `apply()` drives the URL from user action;
  // this handles all other ways the query can change.
  watch(
    () => getRouteQueryParam(route.query, paramName),
    (rawQueryValue) => {
      const parsed = rawQueryValue !== null ? options.fromQuery(rawQueryValue) : null;
      if (parsed !== _value.value) {
        _value.value = parsed;
      }
      nextTick(syncToUrl);
    }
  );

  // reactive() unwraps nested refs at runtime, but Vue 2.7 types don't model this.
  return reactive({
    value: _value,
    isApplied: computed(() => _value.value !== null),
    apply(newInternalValue: string | null): boolean {
      if (newInternalValue === _value.value) return false;
      _value.value = newInternalValue;
      syncToUrl();
      return true;
    },
  }) as unknown as QueryFilter;
}
